import { useContext } from "react";
import { useQuery } from "react-query";

import { UserContext } from "@/components/User";
import {
  CompanyDashboard,
  PrivateDashboard,
  fetchMyTeam,
  fetchOngoingAssignments,
  fetchRecommendedJobs,
  fetchSavedJobs,
} from "@/data/dashboard";
import { fetchOpenApplications, fetchOpenJobs } from "@/data/dashboard/service";

import { useDashboardFormats } from "./useDashboardFormats";

export const useDashboardStats = () => {
  const User = useContext(UserContext);

  const companyId = User.contextCompany?.id;
  const consultantProfileId = User.contextConsultantProfileId;

  const isAdmin = User.hasCompanyAdminPrivileges();
  const isManager = User.hasCompanyManagerPrivileges() && !isAdmin;
  const isPrivateProfile = !isAdmin && !isManager;

  const { formatAdminDashboard, formatManagerDashboard, formatPrivateDashboard } = useDashboardFormats();

  const openApplicationsQuery = useQuery(
    ["open-applications", consultantProfileId, companyId],
    () => fetchOpenApplications({ companyId, consultantProfileId }),
    {
      suspense: false,
    },
  );

  const ongoingAssignmentsQuery = useQuery(
    ["ongoing-assignments", consultantProfileId, companyId],
    () => fetchOngoingAssignments({ companyId, consultantProfileId }),
    {
      enabled: isAdmin || isManager,
      suspense: false,
    },
  );

  const myTeamQuery = useQuery(
    ["my-team", consultantProfileId, companyId],
    () => fetchMyTeam({ companyId, consultantProfileId }),
    {
      enabled: isAdmin || isManager,
      suspense: false,
    },
  );

  const openJobsQuery = useQuery(
    ["open-jobs", consultantProfileId, companyId],
    () => fetchOpenJobs({ companyId, consultantProfileId }),
    {
      suspense: false,
    },
  );

  const recommendedJobsQuery = useQuery(
    ["recommended-jobs", consultantProfileId, companyId],
    () => fetchRecommendedJobs({ companyId, consultantProfileId }),
    {
      enabled: isPrivateProfile,
      suspense: false,
    },
  );

  const savedJobsQuery = useQuery(
    ["saved-jobs", consultantProfileId, companyId],
    () => fetchSavedJobs({ companyId, consultantProfileId }),
    {
      enabled: isPrivateProfile,
      suspense: false,
    },
  );

  const data: CompanyDashboard | PrivateDashboard = {
    openApplications: openApplicationsQuery.isLoading ? -1 : openApplicationsQuery.data?.count ?? 0,
    ongoingAssignments: ongoingAssignmentsQuery.isLoading ? -1 : ongoingAssignmentsQuery.data?.count ?? 0,
    myTeam: myTeamQuery.isLoading ? -1 : myTeamQuery.data?.count ?? 0,
    openJobs: openJobsQuery.isLoading ? -1 : openJobsQuery.data?.count ?? 0,
    recommendedJobs: recommendedJobsQuery.data?.count,
    savedJobs: savedJobsQuery.data?.count,
  };

  return isAdmin
    ? formatAdminDashboard(data)
    : isManager
    ? formatManagerDashboard(data, consultantProfileId)
    : formatPrivateDashboard({
        ...data,
        myOpenApplications: data.openApplications, // TODO remove this?
      });
};
